import { slicedToArray as _slicedToArray } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useState, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { Paragraph, Spacer, TextButton } from '@zillow/constellation';
import PropTypes from 'prop-types';
import { yieldCallback } from '@zillow/yield-callback';
import { useLastValidRefinanceValues, FieldConfig, TOOLTIP, REFINANCE_CALC, refinancePopoverCmsInfo } from './RefinanceCalculator.utils.js';
import { commaSeparateNumber } from '../../utils/zhl-calculator.js';
import TextField from '../Fields/TextField.js';
import CheckboxField from '../Fields/CheckboxField.js';
import LegalPopover from '../CalculatorForm/LegalPopover.js';
import { sendFieldInteractionEvent } from './RefinanceCalculator.clickstream.js';
import useValidFieldUpdate from '../../utils/form/useValidFieldUpdate.js';
import { setServerUrl, createMax, fetchRate } from '../../utils/mortgagehelpers/mortgage-helpers.js';

function RefinanceFields(_ref) {
  var className = _ref.className;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    advanced = _useState2[0],
    setAdvanced = _useState2[1];
  var form = useFormContext();
  var lastValidRefinaneValues = useLastValidRefinanceValues();
  var fieldInterestRate = form.register(FieldConfig.rate.name, FieldConfig.rate.config);
  useEffect(function () {
    // Set the server URL and create the max object
    setServerUrl();
    createMax();
    if (fieldInterestRate) {
      var DOWN_PAYMENT_PERCENTAGE = 0.2;
      var homePrice = lastValidRefinaneValues.fields.newLoanAmount / (1 - DOWN_PAYMENT_PERCENTAGE);
      var getLatestRate = function getLatestRate() {
        return new Promise(function ($return, $error) {
          var rate;
          return Promise.resolve(fetchRate({
            homePrice: homePrice,
            downPayment: Math.round(DOWN_PAYMENT_PERCENTAGE)
          })).then(function ($await_2) {
            try {
              rate = $await_2;
              form.setValue(fieldInterestRate.name, rate);
              return $return();
            } catch ($boundEx) {
              return $error($boundEx);
            }
          }, $error);
        });
      };
      getLatestRate();
    }
  }, []);
  useSyncInterestRate();
  return /*#__PURE__*/React.createElement("div", {
    className: className
  }, /*#__PURE__*/React.createElement(TextField, {
    label: "Current loan amount",
    leftAdornment: "$",
    name: FieldConfig.loanAmount.name,
    required: true,
    config: FieldConfig.loanAmount.config,
    autoComplete: "off",
    format: commaSeparateNumber,
    tooltip: {
      title: 'Current loan amount',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "Enter the remaining balance of your current loan."),
      interactionEventCmsInfo: {
        block_type_cd: TOOLTIP,
        block_id: REFINANCE_CALC,
        block_displayed_txt: 'Current loan amount'
      }
    },
    onChange: function onChange() {
      sendFieldInteractionEvent('Current loan amount', 'form_field');
    }
  }), /*#__PURE__*/React.createElement(TextField, {
    label: "Interest rate",
    rightAdornment: "%",
    name: FieldConfig.rate.name,
    required: true,
    config: FieldConfig.rate.config,
    autoComplete: "off",
    tooltip: {
      title: 'Interest rate',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "The default interest rate is based upon a national, or state specific average from lenders quoting on Zillow for preliminary research purposes only. Actual available rates and monthly payment amounts are subject to market fluctuations and will depend on a number of factors, including geography and loan characteristics. You may enter the current interest rate on your home loan in this field."),
      interactionEventCmsInfo: {
        block_type_cd: TOOLTIP,
        block_id: REFINANCE_CALC,
        block_displayed_txt: 'Interest rate'
      }
    },
    onChange: function onChange() {
      sendFieldInteractionEvent('Interest rate', 'form_field');
    }
  }), /*#__PURE__*/React.createElement(TextField, {
    label: "Current term",
    name: FieldConfig.term.name,
    rightAdornment: "months",
    required: true,
    config: FieldConfig.term.config,
    autoComplete: "off",
    tooltip: {
      title: 'Current term',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "Your current term is the number of months originally set by your lender to pay off your existing mortgage (e.g., 30 years, 20 years, 15 years, etc.). To convert your current term from years to months, multiply the number of years by 12. For instance, a 30-year mortgage term is equal to 360 months."),
      interactionEventCmsInfo: {
        block_type_cd: TOOLTIP,
        block_id: REFINANCE_CALC,
        block_displayed_txt: 'Current term'
      }
    },
    onChange: function onChange() {
      sendFieldInteractionEvent('Current term', 'form_field');
    }
  }), /*#__PURE__*/React.createElement(TextField, {
    label: "Origination year",
    name: FieldConfig.originationYear.name,
    config: FieldConfig.originationYear.config,
    autoComplete: "off",
    required: true,
    tooltip: {
      title: 'Origination year',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "Enter the year you acquired your current mortgage."),
      interactionEventCmsInfo: {
        block_type_cd: TOOLTIP,
        block_id: REFINANCE_CALC,
        block_displayed_txt: 'Origination year'
      }
    },
    onChange: function onChange() {
      sendFieldInteractionEvent('Origination year', 'form_field');
    }
  }), /*#__PURE__*/React.createElement(TextField, {
    label: "New loan amount",
    leftAdornment: "$",
    name: FieldConfig.newLoanAmount.name,
    config: FieldConfig.newLoanAmount.config,
    autoComplete: "off",
    required: true,
    format: commaSeparateNumber,
    tooltip: {
      title: 'New loan amount',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "The new loan balance can be lower or higher if you'd like to pay down the principal or take cash out."),
      interactionEventCmsInfo: {
        block_type_cd: TOOLTIP,
        block_id: REFINANCE_CALC,
        block_displayed_txt: 'New loan amount'
      }
    },
    onChange: function onChange() {
      sendFieldInteractionEvent('New loan amount', 'form_field');
    }
  }), /*#__PURE__*/React.createElement(TextField, {
    label: "New interest rate",
    rightAdornment: "%",
    name: FieldConfig.newRate.name,
    config: FieldConfig.newRate.config,
    autoComplete: "off",
    required: true,
    tooltip: {
      title: 'New interest rate',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "The default interest rate is based upon a national, or state specific average from lenders quoting on Zillow for preliminary research purposes only. Actual available rates and monthly payment amounts are subject to market fluctuations and will depend on a number of factors, including geography and loan characteristics. You may enter a different refi interest rate in this field."),
      interactionEventCmsInfo: {
        block_type_cd: TOOLTIP,
        block_id: REFINANCE_CALC,
        block_displayed_txt: 'New inetrest rate'
      }
    },
    onChange: function onChange() {
      sendFieldInteractionEvent('New interest rate', 'form_field');
    }
  }), /*#__PURE__*/React.createElement(TextField, {
    label: "New term",
    name: FieldConfig.newTerm.name,
    rightAdornment: "months",
    config: FieldConfig.newTerm.config,
    autoComplete: "off",
    required: true,
    format: commaSeparateNumber,
    tooltip: {
      title: 'New term',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "Your new term is the number of months set for you to pay off your new loan, which replaces your existing mortgage during refinancing. The new term may or may not be the same as your current term. To convert your new term from years to months, multiply the number of years by 12. For instance, a 30-year mortgage term is equal to 360 months."),
      interactionEventCmsInfo: {
        block_type_cd: TOOLTIP,
        block_id: REFINANCE_CALC,
        block_displayed_txt: 'New term'
      }
    },
    onChange: function onChange() {
      sendFieldInteractionEvent('New term', 'form_field');
    }
  }), /*#__PURE__*/React.createElement(TextField, {
    label: "Refinance fees",
    leftAdornment: "$",
    name: FieldConfig.fees.name,
    config: FieldConfig.fees.config,
    autoComplete: "off",
    required: true,
    format: commaSeparateNumber,
    tooltip: {
      title: 'Refinance fees',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "Like all home loans, a refinance loan requires closing costs to pay for things like the loan origination fee, title and appraisal. You can roll the fees into the new loan under \"Advanced.\""),
      interactionEventCmsInfo: {
        block_type_cd: TOOLTIP,
        block_id: REFINANCE_CALC,
        block_displayed_txt: 'Refinance fees'
      }
    },
    onChange: function onChange() {
      sendFieldInteractionEvent('Refinance fees', 'form_field');
    }
  }), /*#__PURE__*/React.createElement(Spacer, {
    marginBottom: "xs"
  }, /*#__PURE__*/React.createElement(TextButton, {
    type: "button",
    onClick: yieldCallback(function () {
      setAdvanced(!advanced);
      if (advanced) {
        sendFieldInteractionEvent('Simple', 'toggle');
      } else {
        sendFieldInteractionEvent('Advanced', 'toggle');
      }
    })
  }, advanced ? 'Simple' : 'Advanced')), advanced && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TextField, {
    label: "Cash out",
    leftAdornment: "$",
    name: FieldConfig.cashOut.name,
    config: FieldConfig.cashOut.config,
    autoComplete: "off",
    required: true,
    format: commaSeparateNumber,
    tooltip: {
      title: 'Cash out',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "If you have enough equity in your home, you may be able to take cash out during a refinance."),
      interactionEventCmsInfo: {
        block_type_cd: TOOLTIP,
        block_id: REFINANCE_CALC,
        block_displayed_txt: 'Cash out'
      }
    },
    onChange: function onChange() {
      sendFieldInteractionEvent('Cash out', 'form_field');
    }
  }), /*#__PURE__*/React.createElement(CheckboxField, {
    label: "Roll fees into new loan",
    name: FieldConfig.rollFees.name,
    tooltip: {
      title: 'Roll fees into new loan',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "If you want to avoid any upfront costs of refinancing, you can include those costs as part of your new loan amount to be paid as part of your future mortgage payments. See how those costs will affect your savings."),
      interactionEventCmsInfo: {
        block_type_cd: TOOLTIP,
        block_id: REFINANCE_CALC,
        block_displayed_txt: 'Roll fees into new loan'
      }
    },
    onChange: function onChange() {
      sendFieldInteractionEvent(' Roll fees into new one', 'toggle');
    }
  })), /*#__PURE__*/React.createElement(LegalPopover, {
    triggerText: "Calculator disclaimer",
    text: "The refinance calculator is intended to be used for educational purposes only. Savings are not guaranteed; savings are dependent upon your current loan terms, credit profile, and available lender loan terms. Actual available rates and monthly payment amounts are subject to market fluctuations and will depend on a number of factors, including geography and loan characteristics. The estimates are based on information you provide, and does not include other fees and costs that a lender may assess in addition to monthly principal and interest, such as taxes and insurance and the actual payment obligation may be greater. Zillow Group Marketplace, Inc. does not make loans and this is not a commitment to lend.",
    interactionEventCmsInfo: refinancePopoverCmsInfo
  }));
}

//onchange of new loan amount fetch the interest rate from API.
//functionality to be confirmed from team
function useSyncInterestRate() {
  var form = useFormContext();

  // Watch for changes and do side effects
  var lastValidFields = useRef(null);
  if (lastValidFields.current === null) {
    lastValidFields.current = form.getValues();
  }
  useValidFieldUpdate(form, function (name, value) {
    return new Promise(function ($return, $error) {
      var _Object$assign, DOWN_PAYMENT_PERCENTAGE, homePrice, nextInterestRate;
      lastValidFields.current = Object.assign({}, lastValidFields.current, (_Object$assign = {}, _Object$assign[name] = value, _Object$assign));

      // 1) on change of newLoanAmount, update interest rate based on newLoanAmount
      if (name === FieldConfig.newLoanAmount.name) {
        DOWN_PAYMENT_PERCENTAGE = 0.2;
        homePrice = lastValidFields.current[FieldConfig.newLoanAmount.name] / (1 - DOWN_PAYMENT_PERCENTAGE);
        return Promise.resolve(fetchRate({
          homePrice: homePrice,
          downPayment: Math.round(homePrice * DOWN_PAYMENT_PERCENTAGE)
          // refinance: true,
        })).then(function ($await_3) {
          try {
            nextInterestRate = $await_3;
            if (!isSameFloat(nextInterestRate, lastValidFields.current.rate)) {
              //Update the newRate field
              form.setValue(FieldConfig.newRate.name, nextInterestRate, {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: true
              });
              // Arbitrary amount higher than "new" rate
              form.setValue(FieldConfig.rate.name, nextInterestRate + 0.75, {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: true
              });
            }
            return $If_1.call(this);
          } catch ($boundEx) {
            return $error($boundEx);
          }
        }.bind(this), $error);
      }
      function $If_1() {
        return $return();
      }
      return $If_1.call(this);
    });
  });
}
function isSameFloat(float1, float2) {
  return Math.abs(Number(float1.toFixed(2)) - Number(float2.toFixed(2))) < Number.EPSILON;
}
RefinanceFields.propTypes = process.env.NODE_ENV !== "production" ? {
  className: PropTypes.string
} : {};
RefinanceFields.defaultProps = {
  className: undefined
};

export { RefinanceFields as default };
